import { Link } from "react-router";
import { siteText } from "./content.js";
import { type LegalKind, legalDocs } from "./legal.js";
import { SiteLayout, useSiteLang } from "./SiteLayout.js";
import { LEGAL_REVIEWED, LEGAL_UPDATED } from "./seller.js";

/** Главная для гостя: что это, функции, ссылка на цены. Вошедший пользователь на `/` видит кабинет. */
export function LandingPage() {
  const t = siteText[useSiteLang()];
  return (
    <SiteLayout>
      <section className="py-8">
        <h1 className="mb-4 text-3xl font-semibold sm:text-4xl">{t.hero.title}</h1>
        <p className="mb-6 max-w-2xl text-lg text-slate-600">{t.hero.text}</p>
        <div className="flex flex-wrap gap-3">
          <Link to="/pricing" className="rounded-xl bg-slate-900 px-5 py-3 text-white">
            {t.hero.cta}
          </Link>
          <Link to="/login" className="rounded-xl bg-slate-100 px-5 py-3 text-slate-900">
            {t.hero.login}
          </Link>
        </div>
      </section>
      <section className="py-8">
        <h2 className="mb-4 text-2xl font-semibold">{t.features.title}</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {t.features.items.map((f) => (
            <div key={f.title} className="rounded-2xl border border-slate-200 p-4">
              <h3 className="mb-1 font-medium">{f.title}</h3>
              <p className="text-sm text-slate-600">{f.text}</p>
            </div>
          ))}
        </div>
      </section>
    </SiteLayout>
  );
}

export function PricingPage() {
  const t = siteText[useSiteLang()].pricing;
  return (
    <div>
      <h1 className="mb-6 text-3xl font-semibold">{t.title}</h1>
      <div className="mb-8 rounded-2xl border-2 border-slate-900 p-6">
        <div className="text-sm text-slate-500">{t.plan}</div>
        <div className="mb-4 flex items-baseline gap-2">
          <span className="text-4xl font-semibold">{t.price}</span>
          <span className="text-slate-500">{t.per}</span>
        </div>
        <ul className="mb-6 list-disc space-y-1 pl-5 text-slate-700">
          {t.points.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
        <Link to="/login" className="inline-block rounded-xl bg-slate-900 px-5 py-3 text-white">
          {t.start}
        </Link>
      </div>
      <h2 className="mb-4 text-xl font-semibold">{t.setupTitle}</h2>
      <div className="mb-8 grid gap-4 sm:grid-cols-2">
        {[t.self, t.done].map((p) => (
          <div key={p.title} className="rounded-2xl border border-slate-200 p-4">
            <div className="font-medium">{p.title}</div>
            <div className="mb-2 text-lg">{p.price}</div>
            <p className="text-sm text-slate-600">{p.text}</p>
          </div>
        ))}
      </div>
      <p className="mb-2 text-sm text-slate-500">{t.vat}</p>
      <p className="text-sm text-slate-500">{t.mor}</p>
    </div>
  );
}

function LegalPage({ kind }: { kind: LegalKind }) {
  const lang = useSiteLang();
  const doc = legalDocs[lang][kind];
  const t = siteText[lang];
  return (
    <article className="max-w-3xl">
      {!LEGAL_REVIEWED && <div className="mb-6 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800">{t.draft}</div>}
      <h1 className="mb-2 text-3xl font-semibold">{doc.title}</h1>
      <p className="mb-6 text-sm text-slate-500">
        {t.updated}: {LEGAL_UPDATED}
      </p>
      <p className="mb-6 text-slate-700">{doc.intro}</p>
      {doc.sections.map((s) => (
        <section key={s.heading} id={s.id} className="mb-6 scroll-mt-4">
          <h2 className="mb-2 text-lg font-semibold">{s.heading}</h2>
          {s.paragraphs?.map((p) => (
            <p key={p} className="mb-2 text-slate-700">
              {p}
            </p>
          ))}
          {s.list && (
            <ul className="list-disc space-y-1 pl-5 text-slate-700">
              {s.list.map((li) => (
                <li key={li}>{li}</li>
              ))}
            </ul>
          )}
        </section>
      ))}
    </article>
  );
}

export const TermsPage = () => <LegalPage kind="terms" />;
export const RefundPage = () => <LegalPage kind="refund" />;
export const PrivacyPage = () => <LegalPage kind="privacy" />;
